import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, useAdminGet } from '../kit';
import { Delta, MonthTrend, TrendMetric, fmtMoney, fmtN, fmtP, monthLabel, rate, utcDay, utcStamp } from '../monthly';

/**
 * Analytics → Month over month (owner, 2026-10-01: "month over month
 * insights"). Backed by analytics/monthly (admin_analytics.analytics_monthly):
 * one row per UTC calendar month with the exclusion list applied — signups,
 * sign-ins, active users, searches, saves, contacts, trials, paid plans and
 * revenue — plus the same stretch of the month before for the month still
 * running.
 *
 * Self sign-ups lead, not accounts made: the team adds accounts in bulk
 * (imports, bulk adds) that are most of the new accounts in some months
 * (account_origin tells the two apart).
 */

type Money = Record<string, number> | null;
export interface MoMRow {
  month: string; running?: boolean; through?: string | null; starts?: string | null; billing?: boolean;
  accounts_created: number; admin_added: number; new_signups: number;
  signups_linkedin: number; signups_google: number; signups_email: number;
  first_sign_ins: number; active_users: number; returning_users: number; new_active_users: number;
  sign_in_days: number; searches: number; searchers: number; saves: number; contacts: number;
  trials: number | null; trials_admin: number | null; manual_plans: number | null; paid_new: number | null;
  payments: number | null; revenue: Money; refunds: Money;
  /** Members at the end of the month (now for the month still running):
   *  free = explorer + trial + trial_lapsed + assigned. */
  end_free_members: number; end_explorer: number; end_trial: number; end_trial_lapsed: number;
  end_assigned: number; end_paid: number; end_members: number;
  notes?: string[];
}
interface MoMData {
  months: MoMRow[]; same_period: (MoMRow & { through: string }) | null; available: string[];
  from_month: string; to_month: string; excluded_count: number; billing_admins_excluded: number;
  billing_start: string | null; generated_at: string;
}

/** A row with the derived numbers the views compare. */
const derive = (r: MoMRow) => ({
  ...r,
  end_trial_all: (r.end_trial ?? 0) + (r.end_trial_lapsed ?? 0),
  revenue_inr: r.revenue ? (r.revenue.INR ?? 0) : null,
  refunds_inr: r.refunds ? (r.refunds.INR ?? 0) : null,
  search_rate: rate(r.searchers, r.active_users),
  admin_share: rate(r.admin_added, r.accounts_created),
});
type Row = ReturnType<typeof derive>;

const METRICS: TrendMetric[] = [
  { key: 'end_free_members', label: 'Free members (end)', kind: 'count',
    note: 'cumulative: Explorers + trial accounts + Voyager assigned, as at the end of each month' },
  { key: 'free_split', label: 'Free members by type', kind: 'count', series: [
    { key: 'end_explorer', label: 'Explorers', tone: 'bg-primary-500' },
    { key: 'end_trial_all', label: 'Trial accounts', tone: 'bg-secondary-400' },
    { key: 'end_assigned', label: 'Voyager assigned', tone: 'bg-primary-300' }],
    note: 'as at the end of each month' },
  { key: 'new_signups', label: 'Self sign-ups', kind: 'count', note: 'accounts the person made themselves' },
  { key: 'by_source', label: 'Signups by source', kind: 'count', series: [
    { key: 'signups_linkedin', label: 'LinkedIn', tone: 'bg-secondary-400' },
    { key: 'signups_google', label: 'Google', tone: 'bg-primary-300' },
    { key: 'signups_email', label: 'E-mail / OTP', tone: 'bg-primary-600' }] },
  { key: 'accounts', label: 'Accounts made', kind: 'count', series: [
    { key: 'new_signups', label: 'Self sign-up', tone: 'bg-primary-500' },
    { key: 'admin_added', label: 'Added by admin', tone: 'bg-grayscale-600' }] },
  { key: 'active', label: 'Active users', kind: 'count', series: [
    { key: 'new_active_users', label: 'New', tone: 'bg-primary-500' },
    { key: 'returning_users', label: 'Returning', tone: 'bg-secondary-400' }] },
  { key: 'sign_in_days', label: 'Sign-in days', kind: 'count', note: 'distinct person-days with a sign-in' },
  { key: 'searches', label: 'Searches', kind: 'count' },
  { key: 'searchers', label: 'Searchers', kind: 'count' },
  { key: 'saves', label: 'Saves', kind: 'count' },
  { key: 'contacts', label: 'Uni contacts', kind: 'count' },
  { key: 'trials', label: 'Trials', kind: 'count' },
  { key: 'paid_new', label: 'New paid', kind: 'count' },
  { key: 'revenue_inr', label: 'Revenue (₹)', kind: 'money' },
];

const money = (m: Money, cur = 'INR') => (m ? fmtMoney(m[cur] ?? 0, cur) : '—');
const otherCurrencies = (m: Money) => (m ? Object.keys(m).filter(c => c !== 'INR' && m[c]) : []);

const MonthOverMonth: React.FC = () => {
  const [fromM, setFromM] = useState('');
  const [toM, setToM] = useState('');
  const [selected, setSelected] = useState('');
  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (fromM) p.from_month = fromM;
    if (toM) p.to_month = toM;
    return p;
  }, [fromM, toM]);
  const q = useAdminGet<MoMData>('analytics', 'monthly', params, [JSON.stringify(params)]);
  const d = q.data;
  const rows: Row[] = useMemo(() => (d?.months || []).map(derive), [d]);
  const same = d?.same_period ? derive(d.same_period) : null;

  // What a month is compared with: the month before, or for the month still
  // running, the same stretch of the month before. A month that starts
  // mid-way (the first on record) is not like for like with a whole one.
  const baseOf = (i: number): { base: Row | null; vs: string; unlike: string } => {
    const r = rows[i];
    if (!r || i === 0) return { base: null, vs: '', unlike: '' };
    const prev = rows[i - 1];
    const unlike = r.starts || prev.starts ? 'the first month on record starts mid-month' : '';
    if (r.running && same && same.month === prev.month) {
      return { base: same, vs: `${monthLabel(same.month)} up to ${utcStamp(same.through)} (same stretch)`, unlike };
    }
    return { base: prev, vs: monthLabel(prev.month), unlike };
  };
  const fallback = rows.length ? (rows[rows.length - 1].running && rows.length > 1 ? rows[rows.length - 2] : rows[rows.length - 1]).month : '';
  const sel = rows.some(r => r.month === selected) ? selected : fallback;
  const selIdx = rows.findIndex(r => r.month === sel);
  const cur = selIdx >= 0 ? rows[selIdx] : null;
  const { base, vs, unlike } = baseOf(selIdx);

  const kpi = (label: string, key: keyof Row, kind: 'count' | 'money' = 'count', sub?: string) => {
    const v = cur ? (cur[key] as number | null) : null;
    const b = base ? (base[key] as number | null) : null;
    return (
      <KPI label={label}
        value={<span className="flex items-baseline gap-2">
          <span>{kind === 'money' ? (v === null ? '—' : fmtMoney(v)) : fmtN(v)}</span>
          <Delta cur={v} prev={b} kind="count" vs={vs} unlike={unlike} />
        </span>}
        hint={sub || (base ? `vs ${vs}` : undefined)} />
    );
  };

  const cell = (key: keyof Row, kind: 'count' | 'rate' | 'money' = 'count') => (row: Record<string, any>) => {
    const i = row._i as number;
    const r = rows[i];
    const { base: b, vs: v2, unlike: u } = baseOf(i);
    const val = r[key] as number | null;
    return (
      <div className="flex flex-col leading-tight">
        <span>{kind === 'rate' ? fmtP(val) : kind === 'money' ? (val === null ? '—' : fmtMoney(val)) : fmtN(val)}</span>
        <Delta cur={val} prev={b ? (b[key] as number | null) : null} kind={kind === 'rate' ? 'rate' : 'count'} vs={v2} unlike={u} />
      </div>
    );
  };
  const monthCell = (row: Record<string, any>) => {
    const r = rows[row._i];
    return (
      <button type="button" onClick={() => setSelected(r.month)}
        className={`text-left whitespace-nowrap underline-offset-2 hover:underline ${r.month === sel ? 'font-bold text-grayscale-1300' : ''}`}>
        {monthLabel(r.month)}
        {r.running && <span className="ml-1 text-[10px] rounded border border-grayscale-400 px-1 text-grayscale-1100">to date</span>}
        {r.starts && <span className="ml-1 text-[10px] rounded border border-grayscale-400 px-1 text-grayscale-1100">from {utcDay(r.starts)}</span>}
        {(r.notes || []).length > 0 && <span className="ml-1 text-grayscale-1100 cursor-help" title={(r.notes || []).join('\n')}>ⓘ</span>}
      </button>
    );
  };

  const opts = d?.available || [];
  const monthLabels: Record<string, string> = { '': '12 months back' };
  opts.forEach(m => { monthLabels[m] = monthLabel(m); });
  const growthRows = rows.map((r, i) => ({
    month: r.month, end_free_members: r.end_free_members, end_paid: r.end_paid, new_signups: r.new_signups, accounts_created: r.accounts_created, admin_added: r.admin_added,
    first_sign_ins: r.first_sign_ins, active_users: r.active_users, returning_users: r.returning_users,
    sign_in_days: r.sign_in_days, searches: r.searches, searchers: r.searchers, saves: r.saves, contacts: r.contacts, _i: i,
  }));
  const billingRows = rows.map((r, i) => ({
    month: r.month, trials: r.trials, trials_admin: r.trials_admin, paid_new: r.paid_new, manual_plans: r.manual_plans,
    // In rupees, so the CSV says what the column header says (the cells
    // render from `rows`, in paise, through fmtMoney).
    payments: r.payments, revenue_inr: r.revenue_inr === null ? null : r.revenue_inr / 100,
    refunds_inr: r.refunds_inr === null ? null : r.refunds_inr / 100, _i: i,
  }));

  return (
    <div className="flex flex-col gap-4">
      <Section title="Month over month"
        right={<span className="text-[11px] text-grayscale-1100">UTC months · team accounts on the exclusion list left out</span>}>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">From
            <Select value={fromM} onChange={setFromM} options={['', ...opts]} labels={monthLabels} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1200">To
            <Select value={toM} onChange={setToM} options={['', ...opts]} labels={{ ...monthLabels, '': 'This month' }} />
          </label>
          {(fromM || toM) && <Btn small onClick={() => { setFromM(''); setToM(''); }}>Reset</Btn>}
          {q.loading && d && <span className="text-[11px] text-grayscale-1100 h-9 flex items-center">Updating…</span>}
        </div>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading && !d ? <Loading /> : cur && (
          <>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                {monthLabel(cur.month)}{cur.running ? ' · to date' : ''}
                <span className="ml-2 text-xs font-normal text-grayscale-1100">click a month in the chart or the tables to switch</span>
              </div>
              <div className="text-[11px] text-grayscale-1100">{base ? `▲▼ vs ${vs}` : 'no earlier month in range to compare with'}</div>
            </div>
            <KPIRow>
              {kpi('Free members (end of month)', 'end_free_members', 'count',
                `${fmtN(cur.end_explorer)} explorers · ${fmtN(cur.end_trial_all)} trial · ${fmtN(cur.end_assigned)} assigned`)}
              {kpi('Self sign-ups', 'new_signups', 'count',
                `of ${fmtN(cur.accounts_created)} accounts made · ${fmtN(cur.admin_added)} added by admin`)}
              {kpi('First sign-ins', 'first_sign_ins')}
              {kpi('Active users', 'active_users', 'count', `${fmtN(cur.returning_users)} returning · ${fmtN(cur.new_active_users)} new`)}
              {kpi('Searches', 'searches', 'count', `by ${fmtN(cur.searchers)} people`)}
              {kpi('Trials started', 'trials')}
              {kpi('New paid plans', 'paid_new')}
              {kpi('Revenue', 'revenue_inr', 'money',
                cur.revenue === null ? 'before billing records start'
                  : otherCurrencies(cur.revenue).length ? `+ ${otherCurrencies(cur.revenue).map(c => money(cur.revenue, c)).join(', ')}` : undefined)}
            </KPIRow>
            <div className="rounded-lg border border-grayscale-200 p-3">
              <MonthTrend rows={rows} metrics={METRICS} selected={sel} onSelect={setSelected} />
            </div>
          </>
        )}
      </Section>

      {rows.length > 0 && (
        <Section title="Growth and engagement, per month">
          <DataTable rows={growthRows} maxHeight="none" csvName={`month_over_month_${d?.from_month}_${d?.to_month}`}
            columns={['month', 'end_free_members', 'end_paid', 'new_signups', 'admin_added', 'accounts_created', 'first_sign_ins', 'active_users',
              'returning_users', 'sign_in_days', 'searches', 'searchers', 'saves', 'contacts']}
            labels={{
              month: 'Month', end_free_members: 'Free members (end)', end_paid: 'Paid members (end)',
              new_signups: 'Self sign-ups', admin_added: 'Added by admin', accounts_created: 'Accounts made',
              first_sign_ins: 'First sign-ins', active_users: 'Active users', returning_users: 'Returning',
              sign_in_days: 'Sign-in days', searches: 'Searches', searchers: 'Searchers', saves: 'Saves', contacts: 'Uni contacts',
            }}
            render={{
              month: monthCell, end_free_members: cell('end_free_members'), end_paid: cell('end_paid'),
              new_signups: cell('new_signups'), accounts_created: cell('accounts_created'),
              admin_added: cell('admin_added'), first_sign_ins: cell('first_sign_ins'), active_users: cell('active_users'),
              returning_users: cell('returning_users'), sign_in_days: cell('sign_in_days'), searches: cell('searches'),
              searchers: cell('searchers'), saves: cell('saves'), contacts: cell('contacts'),
            }} />
        </Section>
      )}
      {rows.length > 0 && (
        <Section title="Plans and revenue, per month"
          right={<span className="text-[11px] text-grayscale-1100">
            billing admins left out too{d?.billing_start ? ` · records from ${utcDay(d.billing_start)}` : ''}
          </span>}>
          <DataTable rows={billingRows} maxHeight="none" csvName={`month_over_month_billing_${d?.from_month}_${d?.to_month}`}
            columns={['month', 'trials', 'trials_admin', 'paid_new', 'manual_plans', 'payments', 'revenue_inr', 'refunds_inr']}
            labels={{
              month: 'Month', trials: 'Trials started', trials_admin: 'Trials given by an admin', paid_new: 'New paid plans',
              manual_plans: 'Plans given by an admin', payments: 'Payments', revenue_inr: 'Revenue (₹)', refunds_inr: 'Refunds (₹)',
            }}
            render={{
              month: monthCell, trials: cell('trials'), trials_admin: cell('trials_admin'), paid_new: cell('paid_new'),
              manual_plans: cell('manual_plans'), payments: cell('payments'), revenue_inr: cell('revenue_inr', 'money'),
              refunds_inr: cell('refunds_inr', 'money'),
            }} />
        </Section>
      )}
      {rows.some(r => (r.notes || []).length) && (
        <ul className="text-[11px] text-grayscale-1100 list-disc pl-5">
          {rows.flatMap(r => (r.notes || []).map(n => <li key={r.month + n}><b>{monthLabel(r.month)}:</b> {n}</li>))}
        </ul>
      )}
      {rows.length > 0 && (
        <div className="text-[11px] text-grayscale-1100 flex flex-col gap-1">
          <span>
            <b>Free members (end)</b> = everyone with an account in use who is not paying, as they stood at the end of the month
            (now for the month still running): Explorers, trial accounts (on a trial or lapsed) and Voyager assigned.
            Paid members are counted apart; accounts never signed in are not members.
          </span>
          <span>
            <b>Self sign-ups</b> = accounts the person made: signed up with an e-mail code, LinkedIn / Google or the sign-up
            form, or asked for an invite the team approved. <b>Added by admin</b> = accounts the team made (admin panel, bulk
            add, People import) — recorded as each account is made from now on, worked out for older ones (made without
            anyone signing in). An admin-added account its owner opens later counts under first sign-ins, not sign-ups.
          </span>
          <span>
            <b>Active users</b> = signed in, searched, saved or chatted that month; returning = first signed in before that month.
            <b> Searches, saves, contacts</b> are counted as on the Dashboard.
            <b> Trials</b> and <b>new paid plans</b> count people; revenue is captured payments (₹; other currencies are listed
            beside it, never added in). ▲▼ compare with the month before; the month still running is compared with the same
            stretch of the month before, and ~ marks a comparison that is not like for like.
          </span>
          <span>
            {d?.excluded_count ?? 0} addresses are on the exclusion list (edit it at the bottom of the Dashboard)
            {d?.billing_admins_excluded ? `, plus ${d.billing_admins_excluded} billing admins for plans and revenue` : ''}.
          </span>
        </div>
      )}
    </div>
  );
};

export default MonthOverMonth;
